import { MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { deleteNoteTemplate, getNoteTemplates } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button, Input2 } from 'stories/components'
import { confirm } from 'utils'

import { NoteDetails } from './details'
import type { INoteTemplate } from './types'

export function Notes({ isModal = false, onRawClick }: { isModal?: boolean; onRawClick?: Function }) {
  const [query, setQuery] = useState('')
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [notes, setNotes] = useState<Array<INoteTemplate>>([])
  const [isEditing, setIsEditing] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [selectedIndex, setSelectedIndex] = useState(-1)

  useEffect(() => {
    refresh()
  }, [])

  const refresh = () => {
    setIsLoading(true)
    getNoteTemplates()
      .then(({ data }) => {
        setTotal(data.length)
        setNotes(data)
      })
      .finally(() => setIsLoading(false))
  }

  if (isEditing)
    return (
      <NoteDetails
        lastUpdatedAt={lastUpdatedAt}
        defaults={selectedIndex == -1 ? null : notes[selectedIndex]}
        onBack={() => setIsEditing(false)}
        onComplete={refresh}
      />
    )

  const onAdd = () => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(-1)
    setIsEditing(true)
  }

  const onEdit = (index: number) => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(index)
    setIsEditing(true)
  }

  const onTrash = async (index: number) => {
    const { id, title } = notes[index]
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to remove this note?
        <br />
        <span className="text-gray-600">Note title: {title}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setIsLoading(true)
    deleteNoteTemplate(id)
      .then(refresh)
      .catch(() => setIsLoading(false))
  }

  return (
    <div className="Notess-container">
      {!isModal && (
        <h2 className="text-2xl font-bold flex items-center mb-3">
          Notes
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h2>
      )}
      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              onChange={(value) => setQuery(value)}
            />
          </div>
          <p className="ml-5">- {total} notes</p>
        </div>
        {!isModal && (
          <div className="w-32">
            <Button full onClick={onAdd}>
              Add
            </Button>
          </div>
        )}
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="py-3 px-3">
                Title
              </th>
              <th scope="col" className="px-3">
                Note
              </th>
              {!isModal && (
                <th scope="col" className="px-3">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="">
            {notes.map((item: any, index) => {
              if (query && !`${item.title.toLowerCase()} ${item.notes.toLowerCase()}`.includes(query.toLowerCase()))
                return null
              return (
                <tr
                  className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'} ${
                    isModal ? 'cursor-pointer hover:bg-slate-200' : ''
                  }`}
                  key={`${index}`}
                  onClick={() => isModal && onRawClick && onRawClick(item)}
                >
                  <td scope="row" className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {index + 1}
                  </td>
                  <td className="px-3">{item.title}</td>
                  <td className="px-3">{item.notes}</td>
                  {!isModal && (
                    <td className="px-3">
                      <span className="flex">
                        <span
                          className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onEdit(index)}
                        >
                          <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                        </span>
                        <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onTrash(index)}>
                          <TrashIcon className="w-4 h-4"></TrashIcon>
                        </span>
                      </span>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
